import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'

interface Suppression {
  id: string
  email_address: string
  reason: 'hard_bounce' | 'complaint' | 'manual'
  created_at: string
}

interface SuppressionListResponse {
  data: Suppression[]
  pagination: {
    page: number
    per_page: number
    total: number
  }
}

interface SuppressionListParams {
  search?: string
  page?: number
}

interface AddSuppressionRequest {
  email_address: string
  reason?: string
}

const QUERY_KEY = ['suppressions'] as const

export function useSuppressions(
  params?: SuppressionListParams,
): ReturnType<typeof useQuery<SuppressionListResponse>> {
  return useQuery({
    queryKey: [...QUERY_KEY, params],
    queryFn: () => apiClient.get<SuppressionListResponse>(endpoints.suppressions.list(params)),
  })
}

export function useAddSuppression(): ReturnType<
  typeof useMutation<Suppression, unknown, AddSuppressionRequest>
> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: AddSuppressionRequest) =>
      apiClient.post<Suppression>(endpoints.suppressions.create, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  })
}

export function useRemoveSuppression(): ReturnType<typeof useMutation<unknown, unknown, string>> {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => apiClient.delete(endpoints.suppressions.delete(id)),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  })
}

export type { AddSuppressionRequest, Suppression, SuppressionListParams }
